import React, { useState, useEffect, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaEye,
  FaFileInvoice,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { useNavigate } from "react-router-dom";

const GRNReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";
  const navigate = useNavigate();


  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Form state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [grnNo, setGrnNo] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [status, setStatus] = useState("");
  const [store, setStore] = useState("");
  const [supplier, setSupplier] = useState("");

  // Data state
  const [branchList, setBranchList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [data, setData] = useState([]);

  // localStorage values only
  const storedBranch = localStorage.getItem("branch") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const storedBranchType = (localStorage.getItem("branchType") || "").toUpperCase();

  const initialBranchType =
    storedBranch === "HEAD-OFFICE" || storedBranchType === "HO" ? "HO" : "Branch";

  const [branchType, setBranchType] = useState(initialBranchType);

  // Fetch dropdowns on load
  useEffect(() => {
    axios
      .get(
        `http://nissi-india.com/api/po_fillDropdowns?branchid=${storedBranchId || 0}&branchtype=${
          branchType === "HO" ? "HO" : "BRANCH"
        }`
      )
      .then((res) => {
        const stores = res.data.data?.stores || [];
        const suppliers = res.data.data?.suppliers || [];

        setBranchList(stores);
        setSupplierList(suppliers);
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdowns");
      });
  }, [branchType, storedBranchId]);

  // Format date to dd/MM/yyyy for API
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleSearch = () => {
    if (!store) {
      toast.warn("Please select a Store");
      return;
    }

    setLoading(true);

    const params = {
      supplierid: supplier || "",
      grnno: grnNo || "%",
      fromdate: fromDate ? formatDate(fromDate) : "",
      todate: toDate ? formatDate(toDate) : "",
      status: status || "",
      storeid: "",
      projectid: projectCode || "",
      pur_type: branchType === "HO" ? "H" : "B",
      branchtype: branchType === "HO" ? "HO" : "BRANCH",
      ddl_branch: "Select",
      session_branchid: storedBranchId || "",
    };

    axios
      .get("http://nissi-india.com/api/grn_rpt", { params })
      .then((res) => {
        setData(res.data?.data || []);
        toast.success(`Loaded ${res.data?.data?.length || 0} records`);
      })
      .catch((err) => {
        console.error("GRN fetch failed", err);
        console.error("Request params:", params);
        toast.error("Failed to fetch GRN listing");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePrint = () => {
    const params = {
      supplierid: supplier || "",
      grnno: grnNo || "%",
      fromdate: fromDate ? formatDate(fromDate) : "",
      todate: toDate ? formatDate(toDate) : "",
      status: status || "",
      storeid: "",
      projectid: projectCode || "",
      pur_type: branchType === "HO" ? "H" : "B",
      branchtype: branchType === "HO" ? "HO" : "BRANCH",
      ddl_branch: "Select",
      session_branchid: storedBranchId || "",
    };

    navigate("/inventory/GRN_AllPrint", { state: { params } });
  };

  const handleViewInvoice = (invoiceNo) => {
    axios
      .get("https://www.nissi-india.com/api/grnInvoice_documentsRpt", {
        params: { invoiceno: invoiceNo },
      })
      .then((res) => {
        if (res.data?.fileUrl) {
          window.open(res.data.fileUrl, "_blank");
        } else {
          toast.info("Invoice data loaded. Check console.");
        }
      })
      .catch((err) => {
        console.error("Invoice fetch failed", err);
        toast.error("Failed to load invoice");
      });
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setGrnNo("");
    setProjectCode("");
    setStatus("");
    setStore("");
    setSupplier("");
    setData([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Waiting":
        return "bg-yellow-100 text-yellow-800";
      case "Cancel":
        return "bg-red-100 text-red-800";
      case "Closed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="GRN REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Goods Receipt Note Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and manage all GRN records
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Branch Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3">Branch Type</label>
          <div className="flex gap-2">
            <button
              onClick={() => handleBranchTypeChange("HO")}
              disabled={storedBranch !== "HEAD-OFFICE" && storedBranchType !== "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors 
                ${branchType === "HO" ? primaryButtonClass : secondaryButtonClass}
                ${
                  storedBranch !== "HEAD-OFFICE" && storedBranchType !== "HO"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              `}
            >
              Head Office
            </button>
            <button
              onClick={() => handleBranchTypeChange("Branch")}
              disabled={storedBranch === "HEAD-OFFICE" || storedBranchType === "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors 
                ${branchType === "Branch" ? primaryButtonClass : secondaryButtonClass}
                ${
                  storedBranch === "HEAD-OFFICE" || storedBranchType === "HO"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              `}
            >
              Branch
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">GRN No</label>
                <input
                  type="text"
                  value={grnNo}
                  onChange={(e) => setGrnNo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  placeholder="Enter GRN number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Project Code</label>
                <select
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Project Code</option>
                  <option value="P1">Project 1</option>
                  <option value="P2">Project 2</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Status</option>
                  <option value="Waiting">Waiting</option>
                  <option value="Cancel">Cancel</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Supplier</label>
                <select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Supplier</option>
                  {supplierList.map((s) => (
                    <option key={s.fld_supplierid} value={s.fld_supplierid}>
                      {s.fld_suppliername}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Store <span className="text-red-500">*</span>
                </label>
                <select
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Store</option>
                  {branchList.map((b) => (
                    <option key={b.fld_storeid} value={b.fld_storeid}>
                      {b.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      GRN No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inv No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inv Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((grn, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {grn.fld_grnno}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {grn.fld_date}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {grn.fld_suppliername}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {grn.invno || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {grn.fld_InvDate || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(grn.TotalValueNew)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            grn.Status
                          )}`}
                        >
                          {grn.Status || "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              window.open(
                                `/inventory/GoodsReceiptNote-Print?grnid=${grn.fld_grnid}`,
                                "_blank"
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Print GRN"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewInvoice(grn.invno)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="View Invoice"
                          >
                            <FaFileInvoice className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaEye className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No GRN records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GRNReport;